/**
 * @file kra-writer.ts
 * @description Logic for building Krita (.kra) packages.
 */

import { ZipWriter } from "../zip-writer";

export class KraWriter {
    /**
     * Builds a Krita (.kra) binary package.
     */
    async buildKra(doc: any): Promise<ArrayBuffer> {
        const zip = new ZipWriter();
        const width = doc.width;
        const height = doc.height;
        const layers = doc.layers || [];

        // 1. mimetype (MUST be the first file and uncompressed)
        zip.addFile("mimetype", new TextEncoder().encode("application/x-krita"));

        // 2. maindoc.xml
        const xml = this.generateMainDoc(width, height, layers);
        zip.addFile("maindoc.xml", new TextEncoder().encode(xml));

        // 3. Merged Image (Preview)
        if (doc.composite) {
            const compositePng = await this.imageDataToPng(doc.composite);
            zip.addFile("mergedimage.png", compositePng);
        }

        // 4. Layers
        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            const filename = `layer${i}.png`;
            const pngData = await this.canvasToPng(layer.canvas);
            zip.addFile(`layers/${filename}`, pngData);
        }

        return zip.build();
    }

    private generateMainDoc(w: number, h: number, layers: any[]): string {
        const layerEntries = layers.map((layer, i) => {
            return `  <layer 
                name="${layer.name || "Layer"}" 
                filename="layer${i}.png" 
                visible="${layer.visible !== false ? "1" : "0"}" 
                opacity="${Math.round((layer.opacity !== undefined ? layer.opacity : 1) * 255)}" 
                x="${layer.x || 0}" 
                y="${layer.y || 0}" 
                compositeop="normal" />`;
        }).reverse().join("\n"); // Krita wants bottom-to-top in XML? Actually, top-to-bottom but we'll see.

        return `<?xml version="1.0" encoding="UTF-8"?>
<image xmlns="http://www.calligra.org/DTD/krita" width="${w}" height="${h}" name="HCIE Export" colorspace="RGBA" profile="sRGB-elle-V2-srgbtrc.icc">
 <layers>
${layerEntries}
 </layers>
</image>`;
    }

    private async canvasToPng(canvas: HTMLCanvasElement | ImageData): Promise<Uint8Array> {
        if (canvas instanceof ImageData) {
            const tempCan = document.createElement("canvas");
            tempCan.width = canvas.width;
            tempCan.height = canvas.height;
            tempCan.getContext("2d")?.putImageData(canvas, 0, 0);
            return this.canvasToPng(tempCan);
        }
        
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(new Uint8Array(reader.result as ArrayBuffer));
                };
                reader.readAsArrayBuffer(blob!);
            }, "image/png");
        });
    }

    private async imageDataToPng(data: ImageData): Promise<Uint8Array> {
        return this.canvasToPng(data);
    }
}
